/* eslint-disable camelcase */
import type { I18nKey } from 'common/hooks/useTranslation';

const declinedReasonDescriptionTranslationKeys: Record<string, I18nKey> = {
  amount_limit: 'cards.transactions.declinedReasonDescription_amount_limit',
  card_cancelled: 'cards.transactions.declinedReasonDescription_card_cancelled',
  card_blocked_or_cancelled:
    'cards.transactions.declinedReasonDescription_card_blocked_or_cancelled',
  contactless_limit_reached:
    'cards.transactions.declinedReasonDescription_contactless_limit_reached',
  wallet_funds_insufficient:
    'cards.transactions.declinedReasonDescription_wallet_funds_insufficient',
  regulatory_amount_limit:
    'cards.transactions.declinedReasonDescription_regulatory_amount_limit',
  incorrect_pin_entered:
    'cards.transactions.declinedReasonDescription_incorrect_pin_entered',
  frequency_limit:
    'cards.transactions.declinedReasonDescription_frequency_limit',
  unknown: 'cards.transactions.declinedReasonDescription_unknown',
};

const declinedReasonExplanatoryTranslationKeys: Record<string, I18nKey> = {
  amount_limit: 'cards.transactions.declinedReasonExplanatory_amount_limit',
  amount_limit_physical:
    'cards.transactions.declinedReasonExplanatory_amount_limit_physical',
  amount_limit_subscription:
    'cards.transactions.declinedReasonExplanatory_amount_limit_subscription',
  card_cancelled: 'cards.transactions.declinedReasonExplanatory_card_cancelled',
  card_cancelled_physical:
    'cards.transactions.declinedReasonExplanatory_card_cancelled_physical',
  card_blocked_or_cancelled:
    'cards.transactions.declinedReasonExplanatory_card_blocked_or_cancelled',
  contactless_limit_reached:
    'cards.transactions.declinedReasonExplanatory_contactless_limit_reached',
  wallet_funds_insufficient:
    'cards.transactions.declinedReasonExplanatory_wallet_funds_insufficient',
  regulatory_amount_limit:
    'cards.transactions.declinedReasonExplanatory_regulatory_amount_limit',
  incorrect_pin_entered:
    'cards.transactions.declinedReasonExplanatory_incorrect_pin_entered',
  frequency_limit:
    'cards.transactions.declinedReasonExplanatory_frequency_limit',
};

type DeclinedReasonCode = keyof typeof declinedReasonDescriptionTranslationKeys;

export function getDeclinedReasonDescriptionTranslationKey(
  declinedReasonCode: DeclinedReasonCode,
): I18nKey {
  // Check if the declined reason exists in the mapping
  if (declinedReasonCode in declinedReasonDescriptionTranslationKeys) {
    return declinedReasonDescriptionTranslationKeys[declinedReasonCode];
  }

  // Return a default key for unknown reasons
  return declinedReasonDescriptionTranslationKeys.unknown;
}

export function getDeclinedReasonExplanatoryTranslationKey(
  declinedReasonCode: DeclinedReasonCode,
  cardProduct: 'single_purchase' | 'subscription' | 'physical' | 'multi_use',
): I18nKey {
  // Construct the key based on the card product
  const key = `${declinedReasonCode}_${cardProduct}`;

  // Check if the key exists in the mapping
  if (key in declinedReasonExplanatoryTranslationKeys) {
    return declinedReasonExplanatoryTranslationKeys[key];
  }

  // Fallback to the generic declined reason explanatory key
  return (
    declinedReasonExplanatoryTranslationKeys[declinedReasonCode] ?? undefined
  );
}

export function getDeclinedReasonTranslationKeys(
  declinedReasonCode: DeclinedReasonCode,
  cardProduct: 'single_purchase' | 'subscription' | 'physical' | 'multi_use',
): {
  descriptionKey: I18nKey;
  explanatoryKey: I18nKey;
} {
  return {
    descriptionKey:
      getDeclinedReasonDescriptionTranslationKey(declinedReasonCode),
    explanatoryKey: getDeclinedReasonExplanatoryTranslationKey(
      declinedReasonCode,
      cardProduct,
    ),
  };
}
